import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Switch,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import {
  createGroup,
  deleteGroup,
  listGroups,
  updateGroup,
} from '../../../api/mailingApi';
import { useSelector } from 'react-redux';
import { getUser } from '../../../redux/usersRedux';

const GroupsPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => getUser(state));
  const role = user?.users?.role;
  const userWarehouse = user?.users?.warehouse; // <-- u Ciebie tak wygląda
  const [filters, setFilters] = useState({
    warehouseCode: '',
    messageType: '',
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    severity: 'success',
    msg: '',
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    warehouseCode: '',
    messageType: '',
    name: '',
  });

  const showError = (e, fallback) => {
    const msg =
      e?.response?.data?.message || e?.message || fallback || 'Wystąpił błąd';
    setSnack({ open: true, severity: 'error', msg: String(msg) });
  };

  const showOk = (msg) => setSnack({ open: true, severity: 'success', msg });

  async function load() {
    setLoading(true);
    try {
      const params = {};

      const isAdmin = role === 'ADMIN';

      // Jeśli NIE admin -> wymuś warehouseCode z profilu usera
      if (!isAdmin) {
        if (!userWarehouse) {
          throw new Error('Brak przypisanego magazynu dla użytkownika');
        }
        params.warehouseCode = userWarehouse;
      } else {
        // Admin może filtrować normalnie
        if (filters.warehouseCode.trim())
          params.warehouseCode = filters.warehouseCode.trim();
      }

      if (filters.messageType.trim())
        params.messageType = filters.messageType.trim();

      const data = await listGroups(params);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      showError(e, 'Błąd pobierania grup');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCreate() {
    const payload = {
      warehouseCode: createForm.warehouseCode.trim(),
      messageType: createForm.messageType.trim(),
      name: createForm.name.trim() || undefined,
    };

    if (!payload.warehouseCode || !payload.messageType) {
      setSnack({
        open: true,
        severity: 'error',
        msg: 'warehouseCode i messageType są wymagane',
      });
      return;
    }

    setLoading(true);
    try {
      await createGroup(payload);
      setCreateOpen(false);
      setCreateForm({ warehouseCode: '', messageType: '', name: '' });
      await load();
      showOk('Utworzono grupę');
    } catch (e) {
      showError(e, 'Błąd tworzenia grupy');
    } finally {
      setLoading(false);
    }
  }

  async function onToggleActive(row) {
    setLoading(true);
    try {
      await updateGroup({ id: row.id, isActive: !row.isActive });
      await load();
      showOk('Zaktualizowano status');
    } catch (e) {
      showError(e, 'Błąd zmiany statusu');
    } finally {
      setLoading(false);
    }
  }

  async function onDelete(row) {
    const ok = window.confirm(
      `Usunąć grupę?\n\nWarehouse: ${row.warehouseCode}\nType: ${
        row.messageType
      }\nName: ${row.name || '-'}`,
    );
    if (!ok) return;

    setLoading(true);
    try {
      await deleteGroup(row.id);
      await load();
      showOk('Usunięto grupę');
    } catch (e) {
      showError(e, 'Błąd usuwania grupy');
    } finally {
      setLoading(false);
    }
  }

  const columns = useMemo(
    () => [
      { field: 'warehouseCode', headerName: 'Magazyn', width: 140 },
      { field: 'messageType', headerName: 'Typ komunikatu', width: 220 },
      { field: 'name', headerName: 'Nazwa', flex: 1, minWidth: 220 },
      {
        field: 'isActive',
        headerName: 'Aktywny',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Switch
            checked={Boolean(params.row.isActive)}
            onChange={() => onToggleActive(params.row)}
            disabled={loading}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Akcje',
        width: 260,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/mailing/groups/${params.row.id}`)}
              disabled={loading}
            >
              Otwórz
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onDelete(params.row)}
              disabled={loading}
              color="error"
            >
              Usuń
            </Button>
          </Stack>
        ),
      },
    ],
    [loading, navigate], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h5">Mailing - Grupy</Typography>

        <Paper sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems="center"
          >
            <TextField
              size="small"
              label="Magazyn (warehouseCode)"
              value={
                role === 'ADMIN' ? filters.warehouseCode : userWarehouse || ''
              }
              onChange={(e) =>
                setFilters((s) => ({ ...s, warehouseCode: e.target.value }))
              }
              disabled={role !== 'ADMIN'}
            />
            <TextField
              size="small"
              label="Typ (messageType)"
              value={filters.messageType}
              onChange={(e) =>
                setFilters((s) => ({ ...s, messageType: e.target.value }))
              }
              sx={{ minWidth: 240 }}
            />
            <Button variant="contained" onClick={load} disabled={loading}>
              Filtruj
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setFilters({ warehouseCode: '', messageType: '' });
                setTimeout(load, 0);
              }}
              disabled={loading}
            >
              Reset
            </Button>

            <Box sx={{ flex: 1 }} />

            <Button
              variant="contained"
              onClick={() => setCreateOpen(true)}
              disabled={loading}
            >
              Dodaj grupę
            </Button>
          </Stack>

          {loading ? (
            <Alert
              sx={{ mt: 2 }}
              severity="info"
              icon={<CircularProgress size={16} />}
            >
              Ładowanie...
            </Alert>
          ) : null}
        </Paper>

        <Paper sx={{ height: 560, width: '100%', p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
            }}
          />
        </Paper>
      </Stack>

      {/* CREATE DIALOG */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Dodaj grupę mailingową</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Magazyn (warehouseCode)"
              value={createForm.warehouseCode}
              onChange={(e) =>
                setCreateForm((s) => ({ ...s, warehouseCode: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Typ komunikatu (messageType)"
              value={createForm.messageType}
              onChange={(e) =>
                setCreateForm((s) => ({ ...s, messageType: e.target.value }))
              }
              fullWidth
              helperText="np. PZ04_QUARANTINE"
            />
            <TextField
              label="Nazwa (opcjonalnie)"
              value={createForm.name}
              onChange={(e) =>
                setCreateForm((s) => ({ ...s, name: e.target.value }))
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={loading}>
            Anuluj
          </Button>
          <Button variant="contained" onClick={onCreate} disabled={loading}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupsPage;
